import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecipes } from '@/context/RecipeContext';
import type { InterviewField, InterviewStep as InterviewStepType } from '@/types';
import { cn } from '@/lib/utils';

export function InterviewWizard() {
  const navigate = useNavigate();
  const {
    activeRecipe,
    interviewState,
    updateInterviewResponse,
    advanceInterview,
    goBackInterview,
    completeInterview,
    cancelInterview,
    error,
  } = useRecipes();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (!activeRecipe || !interviewState) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <span className="material-icons text-6xl text-zinc-600 mb-4">error_outline</span>
          <p className="text-zinc-400">No active interview</p>
          <button
            onClick={() => navigate('/recipes')}
            className="mt-4 px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand/90 transition-colors"
          >
            Browse Recipes
          </button>
        </div>
      </div>
    );
  }

  const currentStep = activeRecipe.interview_schema.steps[interviewState.current_step];
  const isLastStep = interviewState.current_step === interviewState.total_steps - 1;
  const isFirstStep = interviewState.current_step === 0;

  const validateStep = (): boolean => {
    const errors: Record<string, string> = {};

    for (const field of currentStep.fields) {
      if (field.required) {
        const value = interviewState.responses[field.id];
        if (value === undefined || value === null || value === '') {
          errors[field.id] = `${field.label} is required`;
        }
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    advanceInterview();
  };

  const handleComplete = async () => {
    if (!validateStep()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    // Create a timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Request timed out. Please try again.')), 30000);
    });

    try {
      // Race between completion and timeout
      const project = await Promise.race([completeInterview(), timeoutPromise]);
      navigate(`/projects/${project.id}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create project';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? Your progress will be lost.')) {
      cancelInterview();
      navigate('/recipes');
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-zinc-800">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button
              onClick={handleCancel}
              className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
            >
              <span className="material-icons text-zinc-400">close</span>
            </button>
            <div>
              <h1 className="text-lg font-bold text-zinc-100">{activeRecipe.name}</h1>
              <p className="text-sm text-zinc-400">{activeRecipe.description}</p>
            </div>
          </div>
        </div>

        {/* Progress */}
        <InterviewProgress
          currentStep={interviewState.current_step}
          totalSteps={interviewState.total_steps}
          steps={activeRecipe.interview_schema.steps}
        />
      </div>

      {/* Step Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-2xl mx-auto">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-zinc-100">{currentStep.name}</h2>
            {currentStep.description && (
              <p className="text-zinc-400 mt-1">{currentStep.description}</p>
            )}
          </div>

          <div className="space-y-6">
            {currentStep.fields.map((field) => (
              <InterviewField
                key={field.id}
                field={field}
                value={interviewState.responses[field.id]}
                onChange={(value) => updateInterviewResponse(field.id, value)}
                error={validationErrors[field.id]}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Error */}
      {(error || submitError) && (
        <div className="mx-6 mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 flex items-center justify-between">
          <span>{error || submitError}</span>
          {submitError && (
            <button
              onClick={() => setSubmitError(null)}
              className="ml-4 hover:text-red-300"
            >
              <span className="material-icons text-sm">close</span>
            </button>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="p-4 border-t border-zinc-800 flex items-center justify-between">
        <button
          onClick={goBackInterview}
          disabled={isFirstStep}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors',
            isFirstStep
              ? 'text-zinc-600 cursor-not-allowed'
              : 'bg-zinc-800 text-zinc-200 hover:bg-zinc-700'
          )}
        >
          <span className="material-icons text-sm">arrow_back</span>
          Back
        </button>

        <div className="flex items-center gap-3">
          <span className="text-sm text-zinc-500">
            Step {interviewState.current_step + 1} of {interviewState.total_steps}
          </span>

          {isLastStep ? (
            <button
              onClick={handleComplete}
              disabled={isSubmitting}
              className="flex items-center gap-2 px-6 py-2 bg-brand text-white rounded-lg hover:bg-brand/90 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Creating...
                </>
              ) : (
                <>
                  <span className="material-icons text-sm">check</span>
                  Create Project
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-6 py-2 bg-brand text-white rounded-lg hover:bg-brand/90 transition-colors"
            >
              Next
              <span className="material-icons text-sm">arrow_forward</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function InterviewProgress({
  currentStep,
  totalSteps,
  steps,
}: {
  currentStep: number;
  totalSteps: number;
  steps: InterviewStepType[];
}) {
  return (
    <div className="flex items-center gap-2">
      {steps.map((step, index) => (
        <div key={step.id} className="flex items-center">
          <div
            className={cn(
              'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors',
              index < currentStep
                ? 'bg-brand text-white'
                : index === currentStep
                ? 'bg-brand/20 text-brand border-2 border-brand'
                : 'bg-zinc-800 text-zinc-500'
            )}
          >
            {index < currentStep ? (
              <span className="material-icons text-sm">check</span>
            ) : (
              index + 1
            )}
          </div>
          {index < totalSteps - 1 && (
            <div
              className={cn(
                'w-8 h-0.5 mx-1',
                index < currentStep ? 'bg-brand' : 'bg-zinc-700'
              )}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function InterviewField({
  field,
  value,
  onChange,
  error,
}: {
  field: InterviewField;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
}) {
  const baseInputClass =
    'w-full bg-zinc-800 border rounded-lg px-4 py-3 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-brand/50 transition-colors';
  const errorClass = error ? 'border-red-500' : 'border-zinc-700';

  return (
    <div>
      <label className="block text-sm font-medium text-zinc-300 mb-2">
        {field.label}
        {field.required && <span className="text-red-400 ml-1">*</span>}
      </label>

      {field.type === 'text' && (
        <input
          type="text"
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className={cn(baseInputClass, errorClass)}
        />
      )}

      {field.type === 'textarea' && (
        <textarea
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          rows={4}
          className={cn(baseInputClass, errorClass, 'resize-none')}
        />
      )}

      {field.type === 'select' && field.options && (
        <select
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value)}
          className={cn(baseInputClass, errorClass)}
        >
          <option value="">{field.placeholder || 'Select an option...'}</option>
          {field.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}

      {field.type === 'color' && (
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={(value as string) || '#4F46E5'}
            onChange={(e) => onChange(e.target.value)}
            className="w-12 h-12 rounded-lg cursor-pointer border-0 bg-transparent"
          />
          <input
            type="text"
            value={(value as string) || '#4F46E5'}
            onChange={(e) => onChange(e.target.value)}
            placeholder="#4F46E5"
            className={cn(baseInputClass, errorClass, 'font-mono')}
          />
        </div>
      )}

      {field.type === 'toggle' && (
        <button
          type="button"
          onClick={() => onChange(!value)}
          className={cn(
            'relative w-14 h-8 rounded-full transition-colors',
            value ? 'bg-brand' : 'bg-zinc-700'
          )}
        >
          <span
            className={cn(
              'absolute top-1 w-6 h-6 rounded-full bg-white transition-transform',
              value ? 'left-7' : 'left-1'
            )}
          />
        </button>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-400">{error}</p>
      )}
    </div>
  );
}
